import { z } from 'zod';
import { ModelProviderSchema, UserCredentialKindSchema } from '@reflex/shared';

// --- Seed credential requirements (canonical Zod definitions) ---
//
// This module is the single source of truth for the seed credential
// requirement contract. `@reflex/plugin-seeds` re-exports these schemas;
// providers and the seed runner share the same inferred types.

/**
 * Portable owner pointer for credentials requiring ownership context.
 * No source DB ids — resolution uses portable identifiers (team slug, user email).
 */
export const SeedCredentialOwnerRefSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('organization') }),
  z.object({ type: z.literal('team'), slug: z.string() }),
  z.object({ type: z.literal('user'), email: z.string() }),
]);
export type SeedCredentialOwnerRef = z.infer<typeof SeedCredentialOwnerRefSchema>;

/**
 * Tier classification for secret credentials. Aligns with the platform's
 * `SecretScope` in `secret-manager.ts`.
 */
export const SeedCredentialTierSchema = z.enum(['service', 'org', 'team', 'user']);
export type SeedCredentialTier = z.infer<typeof SeedCredentialTierSchema>;

/**
 * Common fields present on all credential requirement variants.
 */
const SeedCredentialRequirementBaseSchema = z.object({
  /** Provider kind that requires this credential. */
  seedKind: z.string(),
  /** Whether this credential is required for import to succeed. */
  required: z.boolean(),
  /** Human-readable description of what this credential is for. */
  description: z.string().optional(),
  /** URL to documentation on how to obtain this credential. */
  docsUrl: z.string().optional(),
});

/**
 * A named org/team/user secret (e.g. RUNLOOP_API_KEY).
 */
export const SeedSecretRequirementSchema = SeedCredentialRequirementBaseSchema.extend({
  category: z.literal('secret'),
  /** Secret key name used for matching against supplied credential values. */
  key: z.string(),
  /** Tier: 'org' | 'team' | 'user' — determines who owns the secret. */
  tier: z.enum(['org', 'team', 'user']),
  /** Owner ref for team/user-tier secrets (slug/email). */
  owner: SeedCredentialOwnerRefSchema.optional(),
});
export type SeedSecretRequirement = z.infer<typeof SeedSecretRequirementSchema>;

/**
 * A model-provider credential (API key or subscription).
 */
export const SeedModelProviderRequirementSchema = SeedCredentialRequirementBaseSchema.extend({
  category: z.literal('model-provider'),
  /** Model provider (anthropic, openai, etc.). */
  provider: ModelProviderSchema,
  /** Credential type. */
  credentialType: z.enum(['apiKey', 'subscription']),
  /** Scope: 'org' | 'team' | 'user'. */
  scope: z.enum(['org', 'team', 'user']),
  /** Display name (optional). */
  name: z.string().optional(),
  /** Owner ref for team/user-scope credentials. */
  owner: SeedCredentialOwnerRefSchema.optional(),
});
export type SeedModelProviderRequirement = z.infer<typeof SeedModelProviderRequirementSchema>;

/**
 * Plugin-owned credential identified by a stable resource ref. The value
 * travels in the encrypted artifact under `pluginCredentials[]` and is
 * written to the plugin's scoped secret manager by the owning provider —
 * not through the generic secret service.
 *
 * **Identity model:** `credentialNamespace + ref` uniquely identifies exactly
 * one credential value. The `credentialKind` field is purely descriptive
 * (e.g. "bearer_token", "auth_key") for human readability and is NOT part
 * of the identity key. If a provider needs multiple credential values for
 * the same resource, use distinct `ref` values (e.g. "profile-token" and
 * "profile-secret") rather than same ref with different kinds.
 *
 * `credentialNamespace` is declared by the owning provider's credential specs
 * ({@link SeedCredentialSpec}); the seed runner validates at registration
 * time that every namespace is owned by exactly one provider and rejects
 * requirements whose namespace no registered provider owns.
 */
export const SeedPluginCredentialRequirementSchema = SeedCredentialRequirementBaseSchema.extend({
  category: z.literal('plugin-credential'),
  /**
   * Plugin-declared scoped secret partition namespace. Part of the credential
   * identity key alongside `ref`.
   */
  credentialNamespace: z.string(),
  /**
   * Descriptive credential kind label (e.g. 'auth_key', 'bearer_token').
   * Purely informational — NOT part of the identity key.
   */
  credentialKind: z.string(),
  /**
   * Stable resource identity (profile/config name). Part of the credential
   * identity key alongside `credentialNamespace`.
   */
  ref: z.string(),
});
export type SeedPluginCredentialRequirement = z.infer<typeof SeedPluginCredentialRequirementSchema>;

/**
 * A human OAuth connection that must be re-established on the target.
 * Never carries a value — always resolves to `reconnect_required`.
 */
export const SeedOAuthReconnectRequirementSchema = SeedCredentialRequirementBaseSchema.extend({
  category: z.literal('oauth-reconnect'),
  /** Provider (GitHub, Slack, etc.). */
  provider: z.string(),
  /** Natural key identifying the connection (not local id). */
  connectionKey: z.string(),
});
export type SeedOAuthReconnectRequirement = z.infer<typeof SeedOAuthReconnectRequirementSchema>;

/**
 * A user-private credential blob (claude-max, codex-subscription).
 * Excluded from org migration by default — only resolvable via explicit user-owned import.
 */
export const SeedUserCredentialRequirementSchema = SeedCredentialRequirementBaseSchema.extend({
  category: z.literal('user-credential'),
  /** Kind of credential blob. */
  blobKind: UserCredentialKindSchema,
  /** Owner of the credential (required — user-credentials always have an owner). */
  owner: SeedCredentialOwnerRefSchema,
});
export type SeedUserCredentialRequirement = z.infer<typeof SeedUserCredentialRequirementSchema>;

/**
 * A deployment-wide service-tier credential the operator must have configured.
 * Reported as a target precondition; never treated as tenant-owned or written from a tenant seed.
 */
export const SeedServicePreconditionRequirementSchema = SeedCredentialRequirementBaseSchema.extend({
  category: z.literal('service-precondition'),
  /** Secret key name for this service-tier credential. */
  key: z.string(),
});
export type SeedServicePreconditionRequirement = z.infer<
  typeof SeedServicePreconditionRequirementSchema
>;

/**
 * Discriminated union of all credential requirement variants.
 *
 * - `secret`: org/team/user secret
 * - `model-provider`: model-provider credential
 * - `plugin-credential`: plugin-owned credential addressed by manager + ref
 * - `oauth-reconnect`: human OAuth connection (always reconnect)
 * - `user-credential`: user-private credential blob (excluded by default)
 * - `service-precondition`: deployment-wide service credential (never tenant-owned)
 */
export const SeedCredentialRequirementSchema = z.discriminatedUnion('category', [
  SeedSecretRequirementSchema,
  SeedModelProviderRequirementSchema,
  SeedPluginCredentialRequirementSchema,
  SeedOAuthReconnectRequirementSchema,
  SeedUserCredentialRequirementSchema,
  SeedServicePreconditionRequirementSchema,
]);
export type SeedCredentialRequirement = z.infer<typeof SeedCredentialRequirementSchema>;

/**
 * Resolution mode declared by a provider's {@link SeedCredentialSpec}.
 * The mode determines the default export/import/reuse semantics — a
 * provider gets correct behavior by declaring the mode, with no bespoke
 * credential code:
 *
 * | Mode | Export default | Import default | Target reuse |
 * | --- | --- | --- | --- |
 * | `service-key` | requirement + ref; value via encrypted artifact only | written through the plugin's scoped secret path | reuse if present; non-blank artifact value overwrites |
 * | `single-use` | requirement + ref; **source value never exported** | fresh value required per import | never reuse |
 * | `oauth-human` | connection metadata only; emits `oauth-reconnect` | never writes a value; records a reconnect task; dependent metadata imports proceed | n/a — reconnect on the target (refresh tokens are instance-bound) |
 * | `service-precondition` | reported, never tenant-owned | never written | report present/missing |
 */
export const SeedCredentialResolutionModeSchema = z.enum([
  'service-key',
  'single-use',
  'oauth-human',
  'service-precondition',
]);
export type SeedCredentialResolutionMode = z.infer<typeof SeedCredentialResolutionModeSchema>;

/**
 * @deprecated Use {@link SeedCredentialResolutionModeSchema} instead.
 */
export const SeedCredentialClassSchema = SeedCredentialResolutionModeSchema;
/**
 * @deprecated Use {@link SeedCredentialResolutionMode} instead.
 */
export type SeedCredentialClass = SeedCredentialResolutionMode;
